import { calculateHeaderGroupHeight } from '@theme/critical'
import { Component } from '@theme/component'
import {
  getPageScrollY,
  isPageScrollLocked,
  ScrollLockChangeEvent
} from '@theme/dialog'
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities'

/**
 * @param {boolean} minimumReached
 * @returns {boolean}
 */
export function shouldHideHeaderMenuForOverflow(minimumReached) {
  return minimumReached
}

/**
 * @param {number} windowWidth
 * @param {number | null} menuDrawerHiddenWidth
 * @returns {boolean}
 */
export function shouldRestoreHeaderMenuOnResize(
  windowWidth,
  menuDrawerHiddenWidth
) {
  return menuDrawerHiddenWidth != null && windowWidth > menuDrawerHiddenWidth
}

/**
 * @param {{ scrollTop: number, lastScrollTop: number, headerTop: number }} params
 * @returns {'none' | 'up' | 'down'}
 */
export function resolveAlwaysStickyScrollDirection({
  scrollTop,
  lastScrollTop,
  headerTop
}) {
  if (headerTop >= 0) return 'none'
  if (scrollTop < lastScrollTop) return 'up'
  return 'down'
}

/**
 * Write `--header-height` only when the rounded pixel value changed.
 * Repeated identical writes still invalidate Chrome's Styles pane.
 *
 * @param {number} heightPx
 * @param {HTMLElement | null | undefined} [styleTarget]
 * @returns {boolean} whether the custom property was written
 */
/**
 * Resolve a CSS length token (`px` / `rem`) to pixels.
 *
 * @param {string | number | null | undefined} value
 * @param {number} [rootFontSizePx]
 * @returns {number}
 */
export function parseCssLengthPx(value, rootFontSizePx = 16) {
  const raw = String(value ?? '').trim()
  const amount = Number.parseFloat(raw)

  if (!Number.isFinite(amount)) return 0

  const root =
    Number.isFinite(rootFontSizePx) && rootFontSizePx > 0 ? rootFontSizePx : 16

  if (raw.endsWith('rem')) return amount * root

  return amount
}

/**
 * How far to shift `#header-group` so scroll-hide leaves only a red sliver.
 *
 * @param {number} announcementHeight
 * @param {number} overlap
 * @param {number} sliver
 * @returns {number}
 */
export function resolveAnnouncementHideOffsetPx(
  announcementHeight,
  overlap,
  sliver
) {
  const height = Number(announcementHeight) || 0
  const overlapPx = Number(overlap) || 0
  const sliverPx = Number(sliver) || 0

  return Math.max(0, Math.round(height - overlapPx - sliverPx))
}

/**
 * Measure the announcement bar and write `--header-announcement-hide-distance`.
 *
 * @param {HTMLElement | null | undefined} headerGroup
 * @returns {number}
 */
export function applyAnnouncementHideDistance(headerGroup) {
  if (!(headerGroup instanceof HTMLElement)) return 0

  const announcement = headerGroup.querySelector('.announcement-bar')
  const styles = window.getComputedStyle(headerGroup)
  const rootFontSize = parseCssLengthPx(
    window.getComputedStyle(document.documentElement).fontSize
  )
  const overlap = parseCssLengthPx(
    styles.getPropertyValue('--header-shell-overlap'),
    rootFontSize
  )
  const sliver = parseCssLengthPx(
    styles.getPropertyValue('--header-announcement-sliver'),
    rootFontSize
  )
  const height =
    announcement instanceof HTMLElement ? announcement.offsetHeight : 0
  const distance = resolveAnnouncementHideOffsetPx(height, overlap, sliver)
  const next = `${distance}px`

  if (
    headerGroup.style.getPropertyValue(
      '--header-announcement-hide-distance'
    ) !== next
  ) {
    headerGroup.style.setProperty('--header-announcement-hide-distance', next)
  }

  return distance
}

export function applyHeaderHeightCustomProperty(
  heightPx,
  styleTarget = typeof document === 'undefined' ? null : document.body
) {
  if (!(styleTarget instanceof HTMLElement)) return false

  const next = `${Math.round(Number(heightPx) || 0)}px`

  if (styleTarget.style.getPropertyValue('--header-height') === next) {
    return false
  }

  styleTarget.style.setProperty('--header-height', next)
  return true
}

/**
 * Write `--header-group-height` only when the rounded pixel value changed.
 * Repeated identical writes still invalidate Chrome's Styles pane.
 *
 * @param {number} heightPx
 * @param {HTMLElement | null | undefined} [styleTarget]
 * @returns {boolean} whether the custom property was written
 */
export function applyHeaderGroupHeightCustomProperty(
  heightPx,
  styleTarget = typeof document === 'undefined' ? null : document.body
) {
  if (!(styleTarget instanceof HTMLElement)) return false

  const next = `${Math.round(Number(heightPx) || 0)}px`

  if (styleTarget.style.getPropertyValue('--header-group-height') === next) {
    return false
  }

  styleTarget.style.setProperty('--header-group-height', next)
  return true
}

/**
 * @param {{ scrollTop: number, lastScrollTop: number, currentState?: string }} params
 * @returns {'visible' | 'hidden' | null}
 */
export function resolveHeaderGroupAnnouncementState({
  scrollTop,
  lastScrollTop,
  currentState
}) {
  const isAtTop = scrollTop <= 0
  const isScrollingDown = scrollTop > lastScrollTop
  const isScrollingUp = scrollTop < lastScrollTop

  if (isAtTop || isScrollingUp) {
    return currentState === 'hidden' ? 'visible' : null
  }

  if (isScrollingDown) {
    return currentState !== 'hidden' ? 'hidden' : null
  }

  return null
}

/**
 * Header shell (`header-component`). Companion scripts (`header-mega-menu.js`,
 * `header-drawer.js`, etc.) use `onDocumentLoaded` or custom element lifecycles
 * (`<header-mega-menu>`) and assume the header group is not wholesale-replaced by
 * Section Rendering during normal use.
 */

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerRowTop']

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return

    applyHeaderHeightCustomProperty(entry.target.getBoundingClientRect().height)

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      shouldRestoreHeaderMenuOnResize(
        window.innerWidth,
        this.#menuDrawerHiddenWidth
      )
    ) {
      this.#updateMenuVisibility(false)
    }
  })

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return

    const config = {
      threshold: alwaysSticky ? 1 : 0
    }

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return

      const { isIntersecting } = entry

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active'
        changeMetaThemeColor(this.refs.headerRowTop)
      } else {
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === 'active'
      }
    }, config)

    this.#intersectionObserver.observe(this)
  }

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(
      shouldHideHeaderMenuForOverflow(event.detail.minimumReached)
    )
  }

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    const drawer = /** @type {HTMLElement | undefined} */ (
      this.refs.headerDrawerContainer
    )
    const menu = /** @type {HTMLElement | undefined} */ (this.refs.headerMenu)

    if (!drawer || !menu) return

    if (hideMenu) {
      drawer.classList.remove('desktop:hidden')
      this.#menuDrawerHiddenWidth = window.innerWidth
      menu.classList.add('hidden')
    } else {
      drawer.classList.add('desktop:hidden')
      this.#menuDrawerHiddenWidth = null
      menu.classList.remove('hidden')
    }
  }

  #handleWindowScroll = () => {
    if (isPageScrollLocked()) return

    const stickyMode = this.getAttribute('sticky')
    if (!this.#offscreen && stickyMode !== 'always') return

    const scrollTop = getPageScrollY()
    const isScrollingUp = scrollTop < this.#lastScrollTop
    if (this.#timeout) {
      clearTimeout(this.#timeout)
      this.#timeout = null
    }

    if (stickyMode === 'always') {
      this.dataset.scrollDirection = resolveAlwaysStickyScrollDirection({
        scrollTop,
        lastScrollTop: this.#lastScrollTop,
        headerTop: this.getBoundingClientRect().top
      })

      this.#lastScrollTop = scrollTop
      return
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating')

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false
        this.dataset.stickyState = 'inactive'
        this.dataset.scrollDirection = 'none'
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active'
        this.dataset.scrollDirection = 'up'
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none'
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '')

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle'
        this.removeAttribute('data-animating')
      }, this.#animationDelay)
    } else {
      this.dataset.scrollDirection = 'none'
      this.dataset.stickyState = 'idle'
    }

    this.#lastScrollTop = scrollTop
  }

  connectedCallback() {
    super.connectedCallback()
    this.#resizeObserver.observe(this)
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum)

    const stickyMode = this.getAttribute('sticky')
    if (stickyMode) {
      document.body.setAttribute('data-header-sticky', stickyMode)
      this.#observeStickyPosition(stickyMode === 'always')

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll)
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#resizeObserver.disconnect()
    this.#intersectionObserver?.disconnect()
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum)
    document.removeEventListener('scroll', this.#handleWindowScroll)
    document.body.removeAttribute('data-header-sticky')
    document.body.style.setProperty('--header-height', '0px')
    document.body.style.setProperty('--header-group-height', '0px')
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent)
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component')
  const headerGroup = document.querySelector('#header-group')

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      applyHeaderGroupHeightCustomProperty(
        calculateHeaderGroupHeight(header, headerGroup)
      )
      applyAnnouncementHideDistance(headerGroup)
    })

    resizeObserver.observe(headerGroup)

    // Observe all children of the header group (display: contents fallback)
    const children = headerGroup.children
    for (let i = 0; i < children.length; i++) {
      const element = children[i]
      if (element === header || !(element instanceof HTMLElement)) continue
      resizeObserver.observe(element)
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children
          for (let i = 0; i < children.length; i++) {
            const element = children[i]
            if (element === header || !(element instanceof HTMLElement))
              continue
            resizeObserver.observe(element)
          }
        }
      }
    })

    mutationObserver.observe(headerGroup, { childList: true })
    applyAnnouncementHideDistance(headerGroup)

    /**
     * Offset the header group while scrolling so that part of the
     * announcement bar is hidden on scroll down and revealed again
     * on scroll up.
     *
     * This works by toggling a data attribute that CSS uses to
     * adjust the `top` position of `#header-group`.
     */
    let lastScrollTop = getPageScrollY()
    let ticking = false

    const updateHeaderGroupOffset = () => {
      if (isPageScrollLocked()) return

      const scrollTop = getPageScrollY()
      const isAtTop = scrollTop <= 0
      const isScrollingDown = scrollTop > lastScrollTop
      const isScrollingUp = scrollTop < lastScrollTop

      if (isAtTop || isScrollingUp) {
        if (headerGroup.dataset.announcementState === 'hidden') {
          headerGroup.dataset.announcementState = 'visible'
        }
      } else if (isScrollingDown) {
        if (headerGroup.dataset.announcementState !== 'hidden') {
          headerGroup.dataset.announcementState = 'hidden'
        }
      }

      lastScrollTop = scrollTop
    }

    const handleWindowScroll = () => {
      if (ticking) return
      ticking = true

      requestAnimationFrame(() => {
        updateHeaderGroupOffset()
        ticking = false
      })
    }

    document.addEventListener('scroll', handleWindowScroll, { passive: true })

    document.addEventListener(ScrollLockChangeEvent.eventName, (event) => {
      const detail = /** @type {{ locked?: boolean } | undefined} */ (
        /** @type {CustomEvent} */ (event).detail
      )
      if (detail?.locked) return

      lastScrollTop = getPageScrollY()
      updateHeaderGroupOffset()
    })
  }
})
